import type { VacationOverviewDocument } from '../../models/vacationOverview.types';
import { EXPIRING_WINDOW_DAYS, APPROVAL_LEAD_DAYS, APPROVAL_ALERT_DAYS } from './constants';
import { toIsoDay } from '../overviewDates';
import { differenceInCalendarDays, subDays, parseISO } from 'date-fns';
import type { DisplayDate } from './panoramaDisplayDates';
import type { PeriodRange } from './panoramaPeriod';

export type SegmentDecisionState = 'pending' | 'approved' | 'rejected';

export interface OpenRequestSegment {
	startDate: string;
	endDate: string;
	days: number;
	fraction: number;
	decision: SegmentDecisionState;
}

export function openRequestDisplaySegments(openRequest: any): OpenRequestSegment[] {
	if (!openRequest) return [];
	if (openRequest.segments && openRequest.segments.length > 0) {
		return [...openRequest.segments]
			.sort((a: any, b: any) => a.startDate.localeCompare(b.startDate))
			.map((seg: any, idx: number) => ({
				startDate: seg.startDate,
				endDate: seg.endDate,
				days: seg.days,
				fraction: idx + 1,
				decision: (seg.decision ?? 'pending') as SegmentDecisionState,
			}));
	}
	if (!openRequest.startDate || !openRequest.endDate) return [];
	return [{
		startDate: openRequest.startDate,
		endDate: openRequest.endDate,
		days: openRequest.totalDays,
		fraction: 1,
		decision: 'pending',
	}];
}

export function openRequestSegments(openRequest: any): OpenRequestSegment[] {
	return openRequestDisplaySegments(openRequest).filter(seg => seg.decision !== 'rejected');
}

export function approvalStartDate(openRequest: any): string | null {
	const pending = openRequestDisplaySegments(openRequest).filter(seg => seg.decision === 'pending');
	return pending[0]?.startDate ?? null;
}

export function approvalDeadlineIso(openRequest: any): string | null {
	const startDate = approvalStartDate(openRequest);
	if (!startDate) return null;
	return toIsoDay(subDays(parseISO(startDate), APPROVAL_LEAD_DAYS));
}

export function isApprovalUrgent(openRequest: any, referenceDate: Date): boolean {
	const deadline = approvalDeadlineIso(openRequest);
	if (!deadline) return false;
	return differenceInCalendarDays(parseISO(deadline), referenceDate) <= APPROVAL_ALERT_DAYS;
}

const DECISION_KIND: Record<SegmentDecisionState, DisplayDate['kind']> = {
	pending: 'request',
	approved: 'request-approved',
	rejected: 'request-rejected',
};

export function pickRequestItems(
	doc: VacationOverviewDocument,
	referenceDate: Date,
	status: 'pending' | 'approval-urgent',
	daysToConcessive: number | null,
	range?: PeriodRange
): DisplayDate[] {
	if (!doc.openRequest) return [];

	const segments = openRequestDisplaySegments(doc.openRequest);
	const periodItems: DisplayDate[] = segments
		.filter(seg => !range || (seg.startDate <= range.to && seg.endDate >= range.from))
		.map(seg => ({
			startDate: seg.startDate,
			endDate: seg.endDate,
			days: seg.days,
			kind: DECISION_KIND[seg.decision],
			tone: seg.decision === 'rejected' ? 'muted' : 'default',
			fraction: segments.length > 1 ? seg.fraction : undefined,
		}));

	const deadline = approvalDeadlineIso(doc.openRequest);
	const approvalItem: DisplayDate | null = deadline
		? {
			startDate: deadline,
			endDate: null,
			days: null,
			kind: 'approval-deadline',
			tone: status === 'approval-urgent' ? 'danger' : 'default',
		}
		: null;

	const concessiveItems: DisplayDate[] = [];
	if (doc.balance?.nextDeadline) {
		const daysToDl = daysToConcessive ?? 0;
		concessiveItems.push({
			startDate: doc.balance.nextDeadline,
			endDate: null,
			days: null,
			kind: 'deadline',
			tone: daysToDl <= EXPIRING_WINDOW_DAYS && daysToDl > 0 ? 'warning' : 'muted',
		});
	}

	if (!approvalItem) return [...periodItems, ...concessiveItems];
	if (status === 'approval-urgent') return [approvalItem, ...periodItems, ...concessiveItems];
	return [...periodItems, approvalItem, ...concessiveItems];
}

export function pickPendingDeadlineItems(
	doc: VacationOverviewDocument,
	referenceDate: Date,
	daysToConcessive: number | null
): DisplayDate[] {
	const items: DisplayDate[] = [];
	if (!doc.openRequest) return items;

	if (isApprovalUrgent(doc.openRequest, referenceDate)) {
		const deadline = approvalDeadlineIso(doc.openRequest);
		if (deadline) {
			items.push({ startDate: deadline, endDate: null, days: null, kind: 'approval-deadline', tone: 'danger' });
		}
	}

	if (daysToConcessive !== null && daysToConcessive >= 0 && daysToConcessive <= EXPIRING_WINDOW_DAYS && doc.balance?.nextDeadline) {
		items.push({ startDate: doc.balance.nextDeadline, endDate: null, days: null, kind: 'deadline', tone: 'warning' });
	}

	return items;
}
